const API_BASE = '/api';

// Declare the global shopify object from App Bridge
declare global {
  interface Window {
    shopify?: {
      idToken(): Promise<string>;
    };
  }
}

// Get shop domain from cache, URL params, or null
export function getShopDomain(): string | null {
  // First check cache (set from status API response)
  if (cachedShopDomain) {
    return cachedShopDomain;
  }

  // Fallback to URL params (set by Shopify when loading embedded app)
  const urlParams = new URLSearchParams(window.location.search);
  const shopFromUrl = urlParams.get('shop');
  if (shopFromUrl) {
    cachedShopDomain = shopFromUrl;
    return shopFromUrl;
  }

  return null;
}

// Build Shopify Admin URL for an order
// orderId can be a GID (gid://shopify/Order/123) or just the numeric ID
export function getShopifyOrderUrl(orderId: string): string | null {
  const shop = getShopDomain();
  if (!shop) return null;

  // Extract numeric ID from GID if needed
  const numericId = orderId.includes('/')
    ? orderId.split('/').pop()
    : orderId;

  return `https://${shop}/admin/orders/${numericId}`;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Get session token from App Bridge (global shopify object)
  if (window.shopify?.idToken) {
    try {
      const token = await window.shopify.idToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.warn('Failed to get session token:', error);
    }
  }

  return headers;
}

// Custom error class to preserve API error details
export class ApiError extends Error {
  needsAuth?: boolean;
  needsConfig?: boolean;
  statusCode: number;

  constructor(message: string, statusCode: number, details?: { needsAuth?: boolean; needsConfig?: boolean }) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.needsAuth = details?.needsAuth;
    this.needsConfig = details?.needsConfig;
  }
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      ...authHeaders,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new ApiError(
      errorData.message || errorData.error || 'Request failed',
      response.status,
      { needsAuth: errorData.needsAuth, needsConfig: errorData.needsConfig }
    );
  }

  return response.json();
}

// Types
export interface ShopStatus {
  shopDomain: string;
  shopifyConnected: boolean;
  jitsuConnected: boolean;
  fulfillmentServiceActive: boolean;
  carrierServiceActive: boolean;
  stats: {
    totalFulfillments: number;
    pendingFulfillments: number;
    completedToday: number;
    failedToday: number;
  };
  features: {
    carrierServiceEnabled: boolean;
    readAllOrdersEnabled: boolean;
  };
}

// Cached shop domain (set from status response)
let cachedShopDomain: string | null = null;

export function setShopDomain(domain: string): void {
  cachedShopDomain = domain;
}

export interface ShopSettings {
  jitsuApiKey: string;
  jitsuApiUrl: string;
  jitsuWebhookSecret: string;
  defaultCarrier: string;
  autoFulfill: boolean;
  notifyOnDelivery: boolean;
  // Origin address
  originName: string;
  originAddress1: string;
  originCity: string;
  originState: string;
  originZip: string;
  timezone: string;
  // Default package dimensions
  defaultPackageLength: number;
  defaultPackageWidth: number;
  defaultPackageHeight: number;
  defaultDimensionUnit: 'in' | 'cm';
}

export interface EventLog {
  id: string;
  source: string;
  type: string;
  key: string;
  payload: Record<string, unknown>;
  result: string;
  errorMessage?: string;
  createdAt: string;
}

export interface EventLogResponse {
  events: EventLog[];
  total: number;
  page: number;
  totalPages: number;
}

export interface TestShipmentRequest {
  orderId: string;
  trackingNumber?: string;
}

export interface TestShipmentResponse {
  fulfillmentId: string;
  trackingNumber: string;
}

// API functions
export async function fetchShopStatus(): Promise<ShopStatus> {
  return fetchJson<ShopStatus>('/shop/status');
}

export async function fetchSettings(): Promise<ShopSettings> {
  return fetchJson<ShopSettings>('/shop/settings');
}

export async function updateSettings(settings: ShopSettings): Promise<ShopSettings> {
  return fetchJson<ShopSettings>('/shop/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

export async function fetchEventLogs(params: {
  page?: number;
  limit?: number;
  source?: string[];
  type?: string[];
}): Promise<EventLogResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.source?.length) searchParams.set('source', params.source.join(','));
  if (params.type?.length) searchParams.set('type', params.type.join(','));

  return fetchJson<EventLogResponse>(`/events?${searchParams.toString()}`);
}

export async function createTestShipment(data: TestShipmentRequest): Promise<TestShipmentResponse> {
  return fetchJson<TestShipmentResponse>('/tools/test-shipment', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function syncFulfillmentService(): Promise<{ success: boolean }> {
  return fetchJson<{ success: boolean }>('/tools/sync-fulfillment-service', {
    method: 'POST',
  });
}

export interface RegisterCarrierServiceResponse {
  success: boolean;
  carrierServiceId?: string;
  message?: string;
  error?: string;
  needsAuth?: boolean;
}

export async function registerCarrierService(): Promise<RegisterCarrierServiceResponse> {
  return fetchJson<RegisterCarrierServiceResponse>('/tools/register-carrier-service', {
    method: 'POST',
  });
}

// Jitsu credentials
export interface JitsuCredentials {
  hasCredentials: boolean;
  jitsuApiKey: string;
}

export async function fetchJitsuCredentials(): Promise<JitsuCredentials> {
  return fetchJson<JitsuCredentials>('/shop/jitsu-credentials');
}

export async function saveJitsuCredentials(apiKey: string): Promise<{ success: boolean }> {
  return fetchJson<{ success: boolean }>('/shop/jitsu-credentials', {
    method: 'PUT',
    body: JSON.stringify({ jitsuApiKey: apiKey }),
  });
}

export async function deleteJitsuCredentials(): Promise<{ success: boolean }> {
  return fetchJson<{ success: boolean }>('/shop/jitsu-credentials', {
    method: 'DELETE',
  });
}

// ============================================
// FULFILLMENT ORDERS API
// ============================================

export interface FulfillmentOrderLineItem {
  id: string;
  productTitle: string;
  variantTitle?: string;
  sku?: string;
  quantity: number;
  remainingQuantity: number;
}

export interface FulfillmentOrderDestination {
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  country?: string;
  zip?: string;
  phone?: string;
}

export interface FulfillmentOrder {
  id: string;
  status: string;
  requestStatus: string;
  destination?: FulfillmentOrderDestination;
  lineItems: {
    nodes: FulfillmentOrderLineItem[];
  };
}

export interface OrderWithFulfillmentOrders {
  id: string;
  name: string;
  createdAt: string;
  displayFulfillmentStatus: string;
  customer?: {
    displayName: string;
    email?: string;
  };
  shippingAddress?: {
    firstName?: string;
    lastName?: string;
    company?: string;
    address1?: string;
    address2?: string;
    city?: string;
    province?: string;
    provinceCode?: string;
    country?: string;
    countryCode?: string;
    zip?: string;
    phone?: string;
  };
  totalPriceSet?: {
    shopMoney: {
      amount: string;
      currencyCode: string;
    };
  };
  fulfillmentOrders?: {
    nodes: FulfillmentOrder[];
  };
}

export interface FulfillmentOrdersResponse {
  orders: OrderWithFulfillmentOrders[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor?: string;
    endCursor?: string;
  };
}

export interface ShippingRate {
  serviceLevel: string;
  serviceName: string;
  price: number;
  currency: string;
  estimatedDays?: number;
  estimatedDeliveryDate?: string;
}

export interface RatesResponse {
  rates: ShippingRate[];
  order: {
    id: string;
    name: string;
  };
}

export interface CreateShipmentRequest {
  serviceLevel: string;
  fulfillmentOrderId?: string;
}

export interface CreateShipmentResponse {
  success: boolean;
  shipment: {
    id: string;
    trackingCode: string;
  };
  fulfillment?: {
    id: string;
  };
  bindingId: string;
}

export interface ShipmentDetails {
  id: string;
  jitsuShipmentId?: string;
  orderId: string;
  orderName?: string;
  status: string;
  trackingNumbers: string[];
  trackingUrls: string[];
  carrierCompany?: string;
  labelUrls: string[];
  createdAt: string;
  updatedAt: string;
}

// Fetch fulfillment orders (orders with pending fulfillments)
export async function fetchFulfillmentOrders(params: {
  cursor?: string;
  status?: 'unfulfilled' | 'fulfilled';
  search?: string;
  limit?: number;
}): Promise<FulfillmentOrdersResponse> {
  const searchParams = new URLSearchParams();
  if (params.cursor) searchParams.set('cursor', params.cursor);
  if (params.status) searchParams.set('status', params.status);
  if (params.search) searchParams.set('search', params.search);
  if (params.limit) searchParams.set('limit', String(params.limit));

  const queryString = searchParams.toString();
  return fetchJson<FulfillmentOrdersResponse>(
    `/fulfillment-orders${queryString ? `?${queryString}` : ''}`
  );
}

// Get counts for fulfillments page tabs
export interface FulfillmentCounts {
  pendingCount: number;
  shippedCount: number;
}

export async function fetchFulfillmentCounts(): Promise<FulfillmentCounts> {
  return fetchJson<FulfillmentCounts>('/fulfillments/counts');
}

// Get shipping rates for an order
export async function fetchShippingRates(orderId: string): Promise<RatesResponse> {
  return fetchJson<RatesResponse>(`/orders/${encodeURIComponent(orderId)}/rates`, {
    method: 'POST',
  });
}

// Create shipment for an order
export async function createShipment(
  orderId: string,
  data: CreateShipmentRequest
): Promise<CreateShipmentResponse> {
  return fetchJson<CreateShipmentResponse>(`/orders/${encodeURIComponent(orderId)}/ship`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Get shipment details
export async function fetchShipmentDetails(shipmentId: string): Promise<ShipmentDetails> {
  return fetchJson<ShipmentDetails>(`/shipments/${encodeURIComponent(shipmentId)}`);
}

// Cancel shipment response
export interface CancelShipmentResponse {
  success: boolean;
  shipment: ShipmentDetails;
  jitsuCancelled: boolean;
}

// Cancel a shipment
export async function cancelShipment(shipmentId: string): Promise<CancelShipmentResponse> {
  return fetchJson<CancelShipmentResponse>(`/shipments/${encodeURIComponent(shipmentId)}/cancel`, {
    method: 'POST',
  });
}

// Label response from backend - can have different shapes
interface LabelApiResponse {
  labelUrls?: string[];
  label?: string;
  format: string;
  trackingNumbers?: string[];
  status?: string;
}

// Get label and trigger download
export async function fetchLabelUrl(
  shipmentId: string,
  format: 'PDF' | 'ZPL' | 'PNG' = 'PDF'
): Promise<{ labelUrl: string; format: string }> {
  const response = await fetchJson<LabelApiResponse>(
    `/shipments/${encodeURIComponent(shipmentId)}/label?format=${format}`
  );

  let labelUrl = '';

  // Check if we have a direct URL
  if (response.labelUrls && response.labelUrls.length > 0) {
    const url = response.labelUrls[0];
    if (url.startsWith('http://') || url.startsWith('https://')) {
      labelUrl = url;
    } else {
      // It's base64 content in the URL field
      labelUrl = createBase64DataUrl(url, format);
    }
  } else if (response.label) {
    // Check if it's a URL or base64 content
    if (response.label.startsWith('http://') || response.label.startsWith('https://')) {
      labelUrl = response.label;
    } else if (format === 'ZPL') {
      // ZPL is plain text
      labelUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(response.label)}`;
    } else {
      // PDF or PNG as base64
      labelUrl = createBase64DataUrl(response.label, format);
    }
  }

  return { labelUrl, format: response.format };
}

// Helper to create data URL from base64 content
function createBase64DataUrl(base64Content: string, format: 'PDF' | 'ZPL' | 'PNG'): string {
  // Remove any data URL prefix if already present
  const cleanBase64 = base64Content.replace(/^data:[^;]+;base64,/, '');

  const mimeTypes: Record<string, string> = {
    'PDF': 'application/pdf',
    'PNG': 'image/png',
    'ZPL': 'text/plain',
  };

  return `data:${mimeTypes[format]};base64,${cleanBase64}`;
}

// Download label as a file (for base64 content)
export function downloadLabel(
  base64DataUrl: string,
  filename: string
): void {
  // Create a link element and trigger download
  const link = document.createElement('a');
  link.href = base64DataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Shipment list types
export interface ShipmentListItem {
  id: string;
  orderId: string;
  orderName?: string;
  fulfillmentOrderId: string;
  jitsuShipmentId?: string;
  fulfillmentId?: string;
  status: string;
  trackingNumbers: string[];
  trackingUrls: string[];
  carrierCompany?: string;
  labelUrls: string[];
  // Recipient info
  recipientName?: string;
  recipientCompany?: string;
  recipientAddress?: string;
  recipientCity?: string;
  recipientState?: string;
  recipientZip?: string;
  recipientCountry?: string;
  // Delivery info
  estimatedDeliveryDate?: string;
  serviceLevel?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShipmentsResponse {
  shipments: ShipmentListItem[];
  total: number;
  page: number;
  totalPages: number;
}

// List shipments
export async function fetchShipments(params: {
  status?: string;
  orderId?: string;
  page?: number;
  limit?: number;
  startDate?: string; // ISO date string (YYYY-MM-DD) - created date
  endDate?: string;   // ISO date string (YYYY-MM-DD) - created date
  search?: string;    // Search across order name, tracking number, carrier, recipient
  serviceLevel?: string; // Filter by service level (STANDARD, NEXT_DAY, SAME_DAY)
  deliveryDate?: string; // ISO date string (YYYY-MM-DD) - estimated delivery date
}): Promise<ShipmentsResponse> {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.orderId) searchParams.set('orderId', params.orderId);
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);
  if (params.search) searchParams.set('search', params.search);
  if (params.serviceLevel) searchParams.set('serviceLevel', params.serviceLevel);
  if (params.deliveryDate) searchParams.set('deliveryDate', params.deliveryDate);

  const queryString = searchParams.toString();
  return fetchJson<ShipmentsResponse>(
    `/shipments${queryString ? `?${queryString}` : ''}`
  );
}

// Export shipments as CSV - triggers file download
export async function exportShipmentsCsv(params: {
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  serviceLevel?: string;
  deliveryDate?: string;
}): Promise<void> {
  const authHeaders = await getAuthHeaders();

  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);
  if (params.search) searchParams.set('search', params.search);
  if (params.serviceLevel) searchParams.set('serviceLevel', params.serviceLevel);
  if (params.deliveryDate) searchParams.set('deliveryDate', params.deliveryDate);

  const queryString = searchParams.toString();
  const url = `${API_BASE}/shipments/export${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    headers: authHeaders,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new ApiError(
      errorData.message || errorData.error || 'Export failed',
      response.status
    );
  }

  // Get the CSV content and trigger download
  const csvContent = await response.text();
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const downloadUrl = URL.createObjectURL(blob);

  // Create a temporary link and click it to trigger download
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `shipments-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(downloadUrl);
}

// ============================================
// BULK OPERATIONS
// ============================================

// Bulk ship request/response types
export interface BulkShipRequest {
  orders: Array<{ orderId: string; fulfillmentOrderId?: string }>;
  serviceLevel?: string;
}

export interface BulkShipResult {
  orderId: string;
  success: boolean;
  shipmentId?: string;
  trackingCode?: string;
  bindingId?: string;
  error?: string;
}

export interface BulkShipResponse {
  success: boolean;
  results: BulkShipResult[];
  summary: {
    total: number;
    succeeded: number;
    failed: number;
  };
}

// Bulk create shipments for multiple orders
export async function bulkCreateShipments(
  request: BulkShipRequest
): Promise<BulkShipResponse> {
  return fetchJson<BulkShipResponse>('/orders/bulk-ship', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// Bulk labels request/response types
export interface BulkLabelsRequest {
  shipmentIds: string[];
  format?: 'PDF' | 'ZPL' | 'PNG';
}

export interface BulkLabelResult {
  shipmentId: string;
  trackingCode?: string;
  success: boolean;
  labelUrl?: string;
  error?: string;
}

export interface BulkLabelsResponse {
  success: boolean;
  format: string;
  merged?: boolean;
  mergedLabelUrl?: string;
  labelCount?: number;
  results?: BulkLabelResult[];
  summary: {
    total: number;
    found: number;
    succeeded: number;
    failed: number;
  };
}

// Bulk fetch labels for multiple shipments
export async function bulkFetchLabels(
  request: BulkLabelsRequest
): Promise<BulkLabelsResponse> {
  return fetchJson<BulkLabelsResponse>('/shipments/bulk-labels', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// ============================================
// WEBHOOK SIMULATION (TOOLS)
// ============================================

export interface BindingForSimulation {
  id: string;
  orderId: string;
  orderName?: string;
  jitsuShipmentId?: string;
  status: string;
  trackingNumbers: string[];
  createdAt: string;
}

export interface BindingsResponse {
  bindings: BindingForSimulation[];
}

export interface SimulateWebhookRequest {
  bindingId: string;
  eventType: string;
}

export interface SimulateWebhookResponse {
  success: boolean;
  message: string;
  bindingId: string;
  eventType: string;
  previousStatus: string;
}

// Jitsu webhook event types for simulation
export const JITSU_EVENT_TYPES = [
  { value: 'SHIPMENT.GEOCODED', label: 'Label Created', category: 'Label' },
  { value: 'SHIPMENT.GEOCODE_FAILED', label: 'Geocode Failed', category: 'Label' },
  { value: 'SHIPMENT.SCAN', label: 'Package Scanned', category: 'Receiving' },
  { value: 'SHIPMENT.RECEIVED_OK', label: 'Received OK', category: 'Receiving' },
  { value: 'SHIPMENT.RECEIVED_DAMAGED', label: 'Received Damaged', category: 'Receiving' },
  { value: 'CONTAINER.RECEIVED', label: 'Container Received', category: 'Receiving' },
  { value: 'SHIPMENT.PICKUP_READY', label: 'Pickup Ready', category: 'Pickup' },
  { value: 'SHIPMENT.PICKUP_EN_ROUTE', label: 'Pickup En Route', category: 'Pickup' },
  { value: 'SHIPMENT.PICKUP_SUCCEEDED', label: 'Pickup Succeeded', category: 'Pickup' },
  { value: 'SHIPMENT.PICKUP_FAILED', label: 'Pickup Failed', category: 'Pickup' },
  { value: 'SHIPMENT.DROPOFF_READY', label: 'Dropoff Ready', category: 'Delivery' },
  { value: 'SHIPMENT.DROPOFF_EN_ROUTE', label: 'Out for Delivery', category: 'Delivery' },
  { value: 'SHIPMENT.DROPOFF_DELAY', label: 'Delivery Delayed', category: 'Delivery' },
  { value: 'SHIPMENT.DROPOFF_SUCCEEDED', label: 'Delivered', category: 'Delivery' },
  { value: 'SHIPMENT.DROPOFF_FAILED', label: 'Delivery Failed', category: 'Delivery' },
  { value: 'POD.ACCEPTED', label: 'POD Accepted', category: 'POD' },
  { value: 'POD.NEED_REVIEW', label: 'POD Needs Review', category: 'POD' },
  { value: 'POD.REJECTED', label: 'POD Rejected', category: 'POD' },
  { value: 'SHIPMENT.RETURN_READY', label: 'Return Ready', category: 'Return' },
  { value: 'SHIPMENT.RETURN_EN_ROUTE', label: 'Return En Route', category: 'Return' },
  { value: 'SHIPMENT.RETURN_SUCCEEDED', label: 'Return Succeeded', category: 'Return' },
  { value: 'SHIPMENT.RETURN_FAILED', label: 'Return Failed', category: 'Return' },
  { value: 'SHIPMENT.CANCELLED_BEFORE_PICKUP', label: 'Cancelled (Before Pickup)', category: 'Cancellation' },
  { value: 'SHIPMENT.CANCELLED_AFTER_PICKUP', label: 'Cancelled (After Pickup)', category: 'Cancellation' },
  { value: 'SHIPMENT.MISSING', label: 'Package Missing', category: 'Exception' },
  { value: 'SHIPMENT.DISPOSABLE', label: 'Package Disposable', category: 'Exception' },
] as const;

export async function fetchBindingsForSimulation(search?: string): Promise<BindingsResponse> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  params.set('limit', '20');
  return fetchJson<BindingsResponse>(`/tools/bindings?${params.toString()}`);
}

export async function simulateWebhook(
  request: SimulateWebhookRequest
): Promise<SimulateWebhookResponse> {
  return fetchJson<SimulateWebhookResponse>('/tools/simulate-webhook', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}
